import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { putClient } from "../API/clientAPI";
import { getJobOrders, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import SignatureCanvas from "react-signature-canvas";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AreasTable from "./FormComponents/AreasTable";
import FindingsCard from "./FormComponents/FindingsCard";

// Import S3 configurations and bucket names
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function QcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    chemical_used: [],
    job_date: "",
    // end_date: "",
    start_time: "",
    // end_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const [satisfactionRating, setSatisfactionRating] = useState(0);

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });
  const [otherControlText, setOtherControlText] = useState("");

  // --- FINDINGS – Areas schema helpers ---
  const PEST_KEYS = ["Roaches","Mosq","Rodents","Flies","Ants","Others"];
  const sumPests = (obj = {}) =>
    PEST_KEYS.reduce((s, k) => {
      const v = Number(obj?.[k]);
      return s + (isNaN(v) ? 0 : v);
    }, 0);

  const FindingsAreaRowSchema = z.object({
    location: z.string().trim().min(1, "Location is required"),
    // allow sparse object; coerce numbers; non-negative
    pests: z.record(z.string(), z.coerce.number().nonnegative()).default({}),
  }).superRefine((row, ctx) => {
    if (sumPests(row.pests) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pests"],
        message: "Enter a count for at least one pest type (> 0).",
      });
    }
  });

  const RecItemSchema = z.object({
    option: z.string().min(1, "Please pick a recommendation."),
    otherText: z.string().optional(),
  }).superRefine((val, ctx) => {
    if (val.option === "Other" && !val.otherText?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["otherText"],
        message: "Please specify your recommendation.",
      });
    }
  });

  const QcSchema = z.object({
    // required by your UI/submit
    satisfaction_rating: z.coerce.number()
      .min(1, "Please pick a rating (1–5).")
      .max(5, "Please pick a rating (1–5)."),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.coerce
      .boolean()
      .refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.coerce
      .boolean()
      .refine(v => v === true, "Please click Upload for Accomplished signature."),
    technician_signatory: z.string().min(1, "Please select a technician signatory."),
    acknowledgedClientSignatory: z.string().min(1, "Please enter client representative."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
      }),
    areas_treated_count: z.coerce.number().min(1, "Please select at least one area treated."),
    findings_count: z.coerce.number().min(1, "Please set L/M/H for at least one pest type."),
    findingsAreas: z.array(FindingsAreaRowSchema)
      .min(1, "Add at least one area in Findings"),
    recommendations: z
      .array(RecItemSchema)
      .min(1, "Please add at least one recommendation."),
    control_against_count: z.coerce.number().min(1, "Please select at least one under Control Against."),
    // When "Others" is checked, require the text
    control_against_others: z.coerce.boolean().optional(),
    control_against_others_text: z.string().optional(),
  }).superRefine((data, ctx) => {
    if (data.control_against_others && !data.control_against_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["control_against_others_text"],
        message: 'Please specify "Others" under Control Against.',
      });
    }
  }).passthrough();


  const {
    control,
    register,
    setValue,
    handleSubmit: handleFormSubmit,
    clearErrors,
    setError,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(QcSchema),
    defaultValues: {
      // REQUIRED fields for validation
      areas_treated_count: 0,
      findings_count: 0,
      control_against_count: 0,
      control_against_others: false,
      control_against_others_text: "",
      satisfaction_rating: 0,
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      // existing defaults
      findingsAreas: [
        { location: "", pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 } },
      ],
      recommendations: [{ option: "", otherText: "" }],
    },
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        setSatisfactionRating(data?.satisfaction_rating || 0);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          job_date: data?.job_date || "",
          // end_date: data?.end_date || "",
          start_time: data?.start_time || "",
          // end_time: data?.end_time || getNowHHMM(),   // default to current time
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const toggleEditTech = (techId) => (e) => {
    const checked = e.target.checked;
    setEditSelectedTechIds(prev => checked ? [...prev, techId] : prev.filter(id => id !== techId));
  };

  const saveEditTechs = async () => {
    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, { shouldValidate: true });

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };


  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("")

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Add states for button enable/disable conditions
  const [isReceiptReady, setIsReceiptReady] = useState(true); // Assume true unless conditions apply
  const [isReportReady, setIsReportReady] = useState(true);

  //downloading json
  const downloadJSON = (data, filename = "parameters.json") => {
    const jsonString = JSON.stringify(data, null, 2); // Convert JSON to string with pretty formatting
    const blob = new Blob([jsonString], { type: "application/json" }); // Create a Blob
    const url = URL.createObjectURL(blob); // Create a URL for the Blob
    const link = document.createElement("a"); // Create a link element
    link.href = url;
    link.download = filename; // Set the download file name
    link.click(); // Programmatically click the link to trigger download
    URL.revokeObjectURL(url); // Clean up the URL after download
  };

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  // const handleJObOrderChange = (field, value) => {
  //   setjobOrderParameters((prev) => {
  //     const newState = {
  //       ...prev,
  //       [field]: value,
  //     };

  //     // If changing the start date, ensure end date is not before start date
  //     if (field === 'job_date' && prev.end_date && value && value > prev.end_date) {
  //       newState.end_date = value; // Set end date to start date if it's earlier
  //     }

  //     // If changing the end date, ensure it's not before start date
  //     if (field === 'end_date' && prev.job_date && value && value < prev.job_date) {
  //       return prev; // Don't update if end date is before start date
  //     }

  //     return newState;
  //   });
  // };
  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };


  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ]

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ]

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const [chemical_used, setChemicalUsed] = useState([
    { label: "1", chemical: "" },
    { label: "2", chemical: "" },
    { label: "3", chemical: "" },
    { label: "4", chemical: "" },
    { label: "5", chemical: "" },
    { label: "6", chemical: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const controlAgainstCount = useMemo(
    () => Object.values(controlAgainst).filter(Boolean).length,
    [controlAgainst]
  );

  useEffect(() => {
    setValue("control_against_count", controlAgainstCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("control_against_others", controlAgainst.others, {
      shouldValidate: true,
    });
    setValue("control_against_others_text", otherControlText, {
      shouldValidate: true,
    });
  }, [controlAgainstCount, controlAgainst.others, otherControlText, setValue]);

  // Type of Structure (checkboxes) + side text fields
  const [structureType, setStructureType] = useState({
    residential: false,
    commercialEstablishment: false,
    others: false,
  });

  const [structureNatureOfBusiness, setStructureNatureOfBusiness] = useState("");
  const [structureOthersText, setStructureOthersText] = useState("");

  const handleStructureTypeChange = (field) => (event) => {
    const checked = event.target.checked;
    setStructureType((prev) => ({ ...prev, [field]: checked }));

    // Clear side fields if their checkbox is unchecked
    if (field === "commercialEstablishment" && !checked) setStructureNatureOfBusiness("");
    if (field === "others" && !checked) setStructureOthersText("");
  };

  // Nature of Work
  const [natureOfWork, setNatureOfWork] = useState({
    generalPestControl: false,
    disinfection: false,
    fumigation: false,
    rodentControl: false,
    foggingMisting: false,
    others: false,
  });
  const [natureOfWorkOthersText, setNatureOfWorkOthersText] = useState("");

  const handleNatureOfWorkChange = (field) => (e) => {
    const checked = e.target.checked;
    setNatureOfWork((prev) => ({ ...prev, [field]: checked }));
    if (field === "others" && !checked) setNatureOfWorkOthersText("");
  };

  // --- GPC (General Pest Control) ---
  const [gpcMain, setGpcMain] = useState({
    insects: false,    // Flying and crawling insects
    rodents: false,    // Rodents
    other: false,      // Other pests
  });

  const [gpcInsects, setGpcInsects] = useState({
    cockroach: false,
    flies: false,
    mosquitoes: false,
  });

  const [gpcOtherText, setGpcOtherText] = useState("");

  const handleGpcMainChange = (field) => (e) => {
    const checked = e.target.checked;
    setGpcMain((prev) => ({ ...prev, [field]: checked }));

    // Clear/disable dependents when toggled off
    if (field === "insects" && !checked) {
      setGpcInsects({ cockroach: false, flies: false, mosquitoes: false });
    }
    if (field === "other" && !checked) {
      setGpcOtherText("");
    }
  };

  const handleGpcInsectChange = (field) => (e) => {
    setGpcInsects((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  // --- ACTION TAKEN / TREATMENT DONE ---
  const [actionTaken, setActionTaken] = useState({
    mistSpraying: false,
    thermalFogging: false,
    coldFogging: false,
    larviciding: false,
    gelBaiting: false,
    uvLight: false,
    wiping: false,
    steaming: false,
    others: false,
  });
  const [actionTakenOthersText, setActionTakenOthersText] = useState("");

  const handleActionTakenChange = (field) => (e) => {
    const checked = e.target.checked;
    setActionTaken((prev) => ({ ...prev, [field]: checked }));
    if (field === "others" && !checked) setActionTakenOthersText("");
  };

  // ----- AREAS TREATED: single table layout -----
  const AREAS_TREATED_LAYOUT = [
    ["Office", "Tables", "Computer", "Cabinets", "Drawer"],
    ["Counter Area", "Cash register", "Telephone", "Counter table", null],
    [null, "Signage", "Softdrinks Dispensing machine", null, null],
    [null, "Soda fountain", "Checkout Stands", null, null],
    ["Dining Area", "Tables", "Chairs", "Walls", "Corners"],
    ["Kitchen", "Flooring", "Walling", "Corner", "Ceiling"],
    [null, "Sink", "Cabinets", "Tables", "Freezer"],
    [null, "Oven", "Fridge", "Chiller", "Kitch eq"],
    [null, "Meat counter", "Meat cutting blocks", null, "Pallets"],
    [null, "CR", "Drainage", null, null],
    ["Bedrooms", "Flooring", "Walling", "Bench", null],
    ["Dishwashing Area", "Sink", "Cabinet", "Table", null],
    ["Storage Area", "Freezer", "Coolers", null, null],
    ["Stock Area", "Cabinets", "Corners", null, null],
    ["Locker Room", "Lockers", "Wall", null, null],
    ["Crawl Space", "Stock items", "Fuse boxes", null, null],
    ["CR", "Sides/ Walls", "Switches", null, null],
    ["Receiving Area", "Sofa", "Canals", null, null],
    ["Hallway/ Stairway", "Corners", "Generator Rm", null, null],
    ["Electrical Circuits", "Switch boxes", "Guardhouse", null, null],
    [null, "Receptacle", "Refrigerators", null, null],
    [null, "Drainage", "Sinks", null, null],
    ["Surroundings", "Gas Room", "Electrical Rm", null, null],
    ["Other areas", "Drainage area", "Plants", null, null],
  ];

  // 1-based index key builder: r{row}c{col}  (e.g., r1c3)
  const makeIdxKey = (r, c) => `r${r + 1}c${c + 1}`;

  // Build initial state only for non-empty cells
  const [areasTreated, setAreasTreated] = useState(() => {
    const init = {};
    AREAS_TREATED_LAYOUT.forEach((row, r) =>
      row.forEach((label, c) => {
        if (label) init[makeIdxKey(r, c)] = false;
      })
    );
    return init;
  });

  const handleAreasTreatedToggle = (key) => (e) => {
    const checked = e.target.checked;
    setAreasTreated((prev) => ({ ...prev, [key]: checked }));
    if (key === OTHER_AREAS_KEY && !checked) setOtherAreasText("");
  };

  const serializeAreasTreated = (state) =>
    Object.entries(state)
      .filter(([, checked]) => checked)
      .map(([k]) => {
        const m = k.match(/^r(\d+)c(\d+)$/);
        return m ? { row: Number(m[1]), col: Number(m[2]) } : null;
      })
      .filter(Boolean);

  const [otherAreasText, setOtherAreasText] = useState("");

  // Find the row/col of the "Other areas" cell and compute its index key (1-based)
  const OTHER_AREAS_KEY = useMemo(() => {
    for (let r = 0; r < AREAS_TREATED_LAYOUT.length; r++) {
      for (let c = 0; c < AREAS_TREATED_LAYOUT[r].length; c++) {
        if (AREAS_TREATED_LAYOUT[r][c] === "Other areas") {
          return `r${r + 1}c${c + 1}`;
        }
      }
    }
    return null;
  }, []);

  const selectedAreasCount = React.useMemo(
    () => Object.values(areasTreated).filter(Boolean).length,
    [areasTreated]
  );

  React.useEffect(() => {
    setValue("areas_treated_count", selectedAreasCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [selectedAreasCount, setValue]);

  // --- FINDINGS ---
  const PEST_TYPES = ['Flies', 'Mosquitoes', 'Roaches', 'Ants', 'Rodents'];
  const LEVELS = ['L', 'M', 'H']; // L - Light, M - Medium, H - High

  // e.g. { Flies: 'L', Mosquitoes: null, ... }
  const [infestationFindings, setInfestationFindings] = useState(
    Object.fromEntries(PEST_TYPES.map(p => [p, null]))
  );

  const ratingBtnSx = (selected) => ({
    fontSize: '1.25rem',
    minWidth: '40px',
    height: '40px',
    p: 0,
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    bgcolor: selected ? 'primary.main' : 'transparent',
    color: selected ? 'common.white' : 'text.secondary',
    border: selected ? 'none' : '1px solid',
    borderColor: selected ? 'transparent' : 'divider',
    transform: selected ? 'scale(1.2)' : 'scale(1)',
    '&:hover': {
      bgcolor: selected ? 'primary.dark' : 'grey.100',
    },
    cursor: 'pointer',
  });

  const findingsSelectedCount = useMemo(
    () => Object.values(infestationFindings).filter(v => v === 'L' || v === 'M' || v === 'H').length,
    [infestationFindings]
  );

  useEffect(() => {
    setValue("findings_count", findingsSelectedCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });
  }, [findingsSelectedCount, setValue]);

  // Findings - Areas
  const { fields: findingsAreas, append: appendFindingArea, remove: removeFindingArea } = useFieldArray({
    control,
    name: "findingsAreas",
  });

  useEffect(() => {
    if (findingsAreas.length === 0) {
      appendFindingArea({ location: "", pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 } });
    }
  }, [findingsAreas.length, appendFindingArea]);

  const MAX_FINDINGS_AREAS = 6;

  // --- RECOMMENDATIONS ---
  const MAX_RECOMMENDATIONS = 8;

  const RECOMMENDATION_OPTIONS = [
    { value: "", label: "None" },
    { value: "Keep areas clean and dry", label: "Keep areas clean and dry" },
    { value: "Keep trash bins covered", label: "Keep trash bins covered" },
    { value: "Dispose Garbage Daily", label: "Dispose Garbage Daily" },
    { value: "Seal / Caulking of Cracks and Crevices", label: "Seal / Caulking of Cracks and Crevices" },
    { value: "Keep doors/windows closed", label: "Keep doors/windows closed" },
    { value: "Fix leaking pipes and gutters", label: "Fix leaking pipes and gutters" },
    { value: "Organize Piling and Storage", label: "Organize Piling and Storage" },
    { value: "Make room available for inspection", label: "Make room available for inspection" },
    { value: "Install screens/weather strip", label: "Install screens/weather strip" },
    // Added to support free-text:
    { value: "Other", label: "Other (specify)" },
  ];

  // Dynamic list: up to 8
  const {
    fields: recFields,
    append: appendRec,
    remove: removeRec,
  } = useFieldArray({ control, name: "recommendations" });

  // Watch all recommendation rows to know when to show text fields
  const recValues = useWatch({ control, name: "recommendations" }) || [];

  useEffect(() => {
    if (recFields.length === 0) {
      appendRec({ option: "", otherText: "" });
    }
  }, [recFields.length, appendRec]);

  // --- CUSTOMER'S RESPONSIBILITIES ---
  const [customerResp, setCustomerResp] = useState({
    // SANITATION
    sanitation1: "",
    sanitation2: "",
    sanitation3: "",
    // RODENT ENTRY POINT
    sealing: "",
    barriers: "",
    repairsMaintenance: "",
    renovation: "",
  });

  const handleCustomerRespChange = (field) => (e) =>
    setCustomerResp((prev) => ({ ...prev, [field]: e.target.value }));

  // End time helper
  const getNowHHMM = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  
  // Temporarily store the data URL
  // Save Accomplished By signature and upload to S3
  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        accomplishedBySignatureRef,
        jobOrder.id,
        "accomplished"
      );

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAccomplishedSignatureURL(url);
      // ✅ RHF: uploaded URL + proof that Upload was clicked
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      // Display a success message
      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      // Handle errors and show an error message
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      await trigger(["accomplishedSignature", "accomplishedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the Accomplished By signature without affecting the button states
  const clearAccomplishedSignature = () => {
    if (accomplishedBySignatureRef.current) {
      accomplishedBySignatureRef.current.clear();
      setAccomplishedSignatureURL("");
      setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
      trigger(["accomplishedSignature", "accomplishedUploaded"]);
    }
  };

  // Save Acknowledged By signature and upload to S3
  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      // Resize and upload the signature
      const url = await resizeAndUploadSignature(
        acknowledgedBySignatureRef,
        jobOrder.id,
        "acknowledged"
      );

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      // Update the state with the signature URL
      setAcknowledgedSignatureURL(url);
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      await trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    } finally {
      setIsLoading(false);
    }
  };
  // Clear the Acknowledged By signature
  const clearAcknowledgedSignature = () => {
    if (acknowledgedBySignatureRef.current) {
      acknowledgedBySignatureRef.current.clear();
      setAcknowledgedSignatureURL("");
      setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
      trigger(["acknowledgedSignature", "acknowledgedUploaded"]);
    }
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  const handleGeneratePDF = async (type) => {
    // Merge contact details with jobOrderParameters
    const pdfData = {
      ...jobOrderParameters,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
    };

    console.log("Job Order Parameters:", pdfData);

    try {
      setIsLoading(true);
      const pdfBlob =
        type === "invoice"
          ? await generateReceipts(pdfData)
          : await generateReports(pdfData);
      if (!pdfBlob) {
        throw new Error("Failed to generate PDF blob.");
      }
      const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
      console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

      // Prepare update data with the correct link field
      const updateData = {
        [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
      };

      // Update DynamoDB with the S3 URL
      await patchJobOrderLink(jobOrder.id, updateData);
      console.log(`DynamoDB updated with ${type} URL:`, s3Url);
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    console.log("Generated PDF Blob:", pdfBlob);

    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader =
      type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: `${fileName}`,
      Body: pdfBlob,
      ContentType: "application/pdf",
    };

    console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

    try {
      await s3Uploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  }; 

  // Function to format date
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async () => {
    const generateAttachmentLinks = (links, False) => {
      // Determine which keys to use for the links
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url: `${baseUrl}/${jobOrder.id}_final_receipt.pdf`
      };
    };

    // Generate the attachment links
    const attachmentLinks = generateAttachmentLinks();

    // Create the HTML list items from the links
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key
          .replace(/_url$/, "")
          .replace(/_/g, " ")
          .toUpperCase(); // Remove '_url' and format
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            <body>            
            <p>Sir/Ma'am,</p>
            <p></p>
            <p>Good day!</p>
            <p></p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on  ${formattedDate}.</p>
            <p>We thank you and please let us know if have any questions.</p>
            <p></p>            
            <p>Sincerely,</p>
            <p>Boaz Pest Control - Accounts Team</p>
            <ul>
              ${attachmentLinksHTML}
            </ul>
            <hr />                     
          </body>            
          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;

    const lambdaPayload = {
      // from: "boaz-billing@maestrosystemsinc.com",
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      // Only send email if recipient email exists
      if (jobOrder?.client?.contact?.email?.email) {
        await sendEmailQueue(lambdaPayload);
        console.log("✅ Email notification sent successfully");
      } else {
        console.warn("⚠️ Email notification skipped - no client email address");
      }
    } catch (error) {
      // Log but don't show error to user - email is optional
      console.warn("⚠️ Email notification failed (non-critical):", error.message);
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const selectedPositions = serializeAreasTreated(areasTreated);
    const selectedTechs = jobOrder?.technicians || [];

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      chemical_used,
      technicians: selectedTechs,
      accomplished_by_technician: selectedTechs.map(
        t => `${t.firstname} ${t.lastname}`
      ),
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      technician_signatory: formData.technician_signatory,
      satisfaction_rating: formData.satisfaction_rating,
      control_against: {
        crawling_insects: controlAgainst.crawlingInsects,
        flying_insects:   controlAgainst.flyingInsects,
        rodent_control:   controlAgainst.rodentControl,
        others:           controlAgainst.others,
        others_text:      otherControlText.trim()
      },
      nature_of_work: {
        general_pest_control: natureOfWork.generalPestControl,
        disinfection: natureOfWork.disinfection,
        fumigation: natureOfWork.fumigation,
        rodent_control: natureOfWork.rodentControl,
        fogging_misting: natureOfWork.foggingMisting,
        others: natureOfWork.others,
        others_text: natureOfWorkOthersText.trim(),
      },
      qc_structure_type: {
        residential: structureType.residential,
        commercial_establishment: structureType.commercialEstablishment,
        others: structureType.others,
        nature_of_business: structureNatureOfBusiness.trim(),
        others_text: structureOthersText.trim(),
      },
      gpc: {
        flying_and_crawling_insects: gpcMain.insects,
        rodents: gpcMain.rodents,
        other_pests: gpcMain.other,
        insects_detail: {
          cockroach: gpcInsects.cockroach,
          flies: gpcInsects.flies,
          mosquitoes: gpcInsects.mosquitoes,
        },
        other_pests_text: gpcOtherText.trim(),
      },
      action_taken_treatment_done: {
        mist_spraying: actionTaken.mistSpraying,
        thermal_fogging: actionTaken.thermalFogging,
        cold_fogging: actionTaken.coldFogging,
        larviciding: actionTaken.larviciding,
        gel_baiting: actionTaken.gelBaiting,
        uv_light: actionTaken.uvLight,
        wiping: actionTaken.wiping,
        steaming: actionTaken.steaming,
        others: actionTaken.others,
        others_text: actionTakenOthersText.trim(),
      },
      areas_treated_positions: selectedPositions,
      areas_treated_other_text: otherAreasText.trim(),
      customers_responsibilities: {
        sanitation: [customerResp.sanitation1, customerResp.sanitation2, customerResp.sanitation3],
        rodent_entry_point: {
          sealing: customerResp.sealing,
          barriers: customerResp.barriers          
        },
        repairs_maintenance: customerResp.repairsMaintenance,
        renovation: customerResp.renovation,
      },
      findings_degree_of_infestation: {
        flies: infestationFindings.Flies,
        mosquitoes: infestationFindings.Mosquitoes,
        roaches: infestationFindings.Roaches,
        ants: infestationFindings.Ants,
        rodents: infestationFindings.Rodents,
      },
      findings_areas: formData.findingsAreas || [],
      recommendations: formData.recommendations || [],
      service_report_status: "Done",
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      setLinks(
        {
          certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
          receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`
        }
      );

      // Send the email after updating links

      setSuccessMessage(
        "Service Report and documents and generated successfully! Redirecting..."
      );

      setIsLoading(false);
      // analysis
      await handleSendEmail();

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
      // All parameters saved successfully

      // Redirect to service reports list after 2 seconds
      setTimeout(() => {
        navigate("/manage-service-reports");
      }, 2000);
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }
  }

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Basic Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <FormControl fullWidth size="small" error={!!errors?.status}>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) =>
                          handleJObOrderChange("status", e.target.value)
                        }
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                      {errors?.status && <FormHelperText error>{errors.status.message}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={12} xl={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address.house_unit_street || ""
                              }, ${jobOrder?.client?.address.barangay || ""
                              }, ${jobOrder?.client?.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12} xl={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""
                          }`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder?.client?.contact?.email?.email || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact?.number
                          ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>           
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobTypes.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Description</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobDescriptions.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder?.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Nature of Work Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    NATURE OF WORK
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={natureOfWork.generalPestControl}
                        onChange={handleNatureOfWorkChange("generalPestControl")}
                      />
                    }
                    label="General Pest Control"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={natureOfWork.disinfection}
                        onChange={handleNatureOfWorkChange("disinfection")}
                      />
                    }
                    label="Disinfection"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={natureOfWork.fumigation}
                        onChange={handleNatureOfWorkChange("fumigation")}
                      />
                    }
                    label="Fumigation"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={natureOfWork.rodentControl}
                        onChange={handleNatureOfWorkChange("rodentControl")}
                      />
                    }
                    label="Rodent Control"
                  />
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={natureOfWork.foggingMisting}
                        onChange={handleNatureOfWorkChange("foggingMisting")}
                      />
                    }
                    label="Fogging/Misting"
                  />

                  {/* Others with text field beside it */}
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={natureOfWork.others}
                          onChange={handleNatureOfWorkChange("others")}
                        />
                      }
                      label="Others"
                    />
                    <TextField
                      label="Please specify"
                      value={natureOfWorkOthersText}
                      onChange={(e) => setNatureOfWorkOthersText(e.target.value)}
                      disabled={!natureOfWork.others}
                      size="small"
                      sx={{ maxWidth: 320 }}
                    />
                  </Box>
                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Structure Type Section */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  TYPE OF STRUCTURE
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                {/* Residential */}
                <FormControlLabel
                  sx={{ width: "100%" }}
                  control={
                    <Checkbox
                      checked={structureType.residential}
                      onChange={handleStructureTypeChange("residential")}
                    />
                  }
                  label="Residential"
                />

                {/* Commercial Establishment + Nature of Business text field */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={structureType.commercialEstablishment}
                        onChange={handleStructureTypeChange("commercialEstablishment")}
                      />
                    }
                    label="Commercial Establishment"
                  />
                  <TextField
                    label="Nature of Business"
                    size="small"
                    sx={{ maxWidth: 320 }}
                    disabled={!structureType.commercialEstablishment}
                    value={structureNatureOfBusiness}
                    onChange={(e) => setStructureNatureOfBusiness(e.target.value)}
                  />
                </Box>

                {/* Others + specify text field */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={structureType.others}
                        onChange={handleStructureTypeChange("others")}
                      />
                    }
                    label="Others"
                  />
                  <TextField
                    label="Please specify"
                    size="small"
                    sx={{ maxWidth: 320 }}
                    disabled={!structureType.others}
                    value={structureOthersText}
                    onChange={(e) => setStructureOthersText(e.target.value)}
                  />
                </Box>
              </FormGroup>
            </CardContent>
          </Card>

          {/* For General Pest Control (GPC) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    For General Pest Control (GPC)
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup>

                  {/* Main: Flying & crawling insects */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gpcMain.insects}
                        onChange={handleGpcMainChange('insects')}
                      />
                    }
                    label="Flying and crawling insects"
                  />

                  {/* Sub-options (enabled only when insects is checked) */}
                  <Box
                    sx={{
                      ml: 4,
                      mt: 0.5,
                      pl: 1,
                      borderLeft: gpcMain.insects ? '2px solid' : '2px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            disabled={!gpcMain.insects}
                            checked={gpcInsects.cockroach}
                            onChange={handleGpcInsectChange('cockroach')}
                          />
                        }
                        label="Cockroach"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            disabled={!gpcMain.insects}
                            checked={gpcInsects.flies}
                            onChange={handleGpcInsectChange('flies')}
                          />
                        }
                        label="Flies"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            disabled={!gpcMain.insects}
                            checked={gpcInsects.mosquitoes}
                            onChange={handleGpcInsectChange('mosquitoes')}
                          />
                        }
                        label="Mosquitoes"
                      />
                    </Stack>
                  </Box>

                  {/* Main: Rodents */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gpcMain.rodents}
                        onChange={handleGpcMainChange('rodents')}
                      />
                    }
                    label="Rodents"
                  />

                  {/* Main: Other pests + text field beside it */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={gpcMain.other}
                          onChange={handleGpcMainChange('other')}
                        />
                      }
                      label="Other pests"
                    />
                    <TextField
                      label="Please specify"
                      size="small"
                      disabled={!gpcMain.other}
                      value={gpcOtherText}
                      onChange={(e) => setGpcOtherText(e.target.value)}
                      sx={{ flex: '1 1 260px', maxWidth: 360 }}
                    />
                  </Box>

                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* ACTION TAKEN / TREATMENT DONE */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    ACTION TAKEN / TREATMENT DONE
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.mistSpraying}
                        onChange={handleActionTakenChange('mistSpraying')}
                      />
                    }
                    label="Mist spraying"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.thermalFogging}
                        onChange={handleActionTakenChange('thermalFogging')}
                      />
                    }
                    label="Thermal fogging"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.coldFogging}
                        onChange={handleActionTakenChange('coldFogging')}
                      />
                    }
                    label="Cold fogging"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.larviciding}
                        onChange={handleActionTakenChange('larviciding')}
                      />
                    }
                    label="Larviciding"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.gelBaiting}
                        onChange={handleActionTakenChange('gelBaiting')}
                      />
                    }
                    label="Gel baiting"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.uvLight}
                        onChange={handleActionTakenChange('uvLight')}
                      />
                    }
                    label="UV light"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.wiping}
                        onChange={handleActionTakenChange('wiping')}
                      />
                    }
                    label="Wiping"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={actionTaken.steaming}
                        onChange={handleActionTakenChange('steaming')}
                      />
                    }
                    label="Steaming"
                  />

                  {/* Others + text field beside it */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={actionTaken.others}
                          onChange={handleActionTakenChange('others')}
                        />
                      }
                      label="Others:"
                    />
                    <TextField
                      label="Please specify"
                      size="small"
                      disabled={!actionTaken.others}
                      value={actionTakenOthersText}
                      onChange={(e) => setActionTakenOthersText(e.target.value)}
                      sx={{ flex: '1 1 260px', maxWidth: 360 }}
                    />
                  </Box>

                </FormGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* AREAS TREATED Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    AREAS TREATED
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AreasTable
                    layout={AREAS_TREATED_LAYOUT}
                    state={areasTreated}
                    onToggle={handleAreasTreatedToggle}
                    ariaLabel="areas_treated"
                    getKey={(rIdx, cIdx /*, label */) => makeIdxKey(rIdx, cIdx)}
                  />

                  {OTHER_AREAS_KEY && (
                    <TextField
                      label="Please specify (Other areas)"
                      size="small"
                      fullWidth
                      sx={{ mt: 1 }}
                      disabled={!areasTreated[OTHER_AREAS_KEY]}
                      value={otherAreasText}
                      onChange={(e) => setOtherAreasText(e.target.value)}
                    />
                  )}
                </Box>
              </CardContent>
              {errors?.areas_treated_count && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.areas_treated_count.message}
                </FormHelperText>
              )}
            </Card>
          </Grid>
          {/* Hidden field that RHF validates */}
          <input
            type="hidden"
            {...register("areas_treated_count", {
              validate: (v) => Number(v) >= 1 || "Please select at least one area treated."
            })}
          />

          {/* FINDINGS */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    FINDINGS
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                {/* Sub-header */}
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Degree of Infestation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  L - Light, M - Medium, H - High
                </Typography>

                {/* Pest rows */}
                <Grid container spacing={1.5}>
                  {PEST_TYPES.map((pest) => (
                    <Grid item xs={12} key={pest}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.25,
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {pest}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 4 }}>
                          {LEVELS.map((lv) => {
                            const selected = infestationFindings[pest] === lv;
                            return (
                              <Button
                                key={lv}
                                onClick={() =>
                                  setInfestationFindings((prev) => ({ ...prev, [pest]: lv }))
                                }
                                sx={ratingBtnSx(selected)}
                                aria-label={`${pest} ${lv}`}
                              >
                                {lv}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {errors?.findings_count && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.findings_count.message}
                  </FormHelperText>
                )}
                <input
                  type="hidden"
                  {...register("findings_count")}
                />

                {/* FINDINGS – Areas list */}
                <Box sx={{ mt: 2 }}>
                  {findingsAreas.map((f, idx) => (
                    <FindingsCard
                      key={f.id}
                      control={control}
                      register={register}
                      index={idx}
                      canRemove={findingsAreas.length > 1}
                      removeFinding={() => {
                        if (findingsAreas.length > 1) removeFindingArea(idx);
                      }}
                    />
                  ))}

                  <Button
                    variant="outlined"
                    size="small"
                    disabled={findingsAreas.length >= MAX_FINDINGS_AREAS}
                    onClick={() => {
                      if (findingsAreas.length >= MAX_FINDINGS_AREAS) return; // hard cap
                      appendFindingArea({
                        location: "",
                        pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
                      });
                    }}
                  >
                    Add Area ({findingsAreas.length}/{MAX_FINDINGS_AREAS})
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* RECOMMENDATIONS */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    RECOMMENDATIONS
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="body2" color="text.secondary">
                    Add up to {MAX_RECOMMENDATIONS} recommendations
                  </Typography>                  
                </Box>

                <Grid container spacing={1.5}>
                  {recFields.map((f, idx) => {
                    const selected = recValues?.[idx]?.option ?? "";

                    return (
                      <Grid item xs={12} key={f.id}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          {/* Dropdown + helper */}
                          <Controller
                            control={control}
                            name={`recommendations.${idx}.option`}
                            defaultValue={f.option ?? ""}
                            render={({ field }) => {
                              const optError = errors?.recommendations?.[idx]?.option;
                              return (
                                <FormControl
                                  size="small"
                                  error={!!optError}
                                  sx={{ minWidth: 260 }}
                                >
                                  <Select
                                    {...field}
                                    displayEmpty
                                    onChange={(e) => {
                                      field.onChange(e);
                                      if (e.target.value !== "Other") {
                                        // clear free-text when leaving "Other"
                                        setValue(`recommendations.${idx}.otherText`, "");
                                        // and clear any stale error on otherText
                                        clearErrors(`recommendations.${idx}.otherText`);
                                      }
                                    }}
                                    renderValue={(val) => (val ? val : "Select recommendation")}
                                  >
                                    {RECOMMENDATION_OPTIONS.map((opt) => (
                                      <MenuItem key={opt.value || "none"} value={opt.value}>
                                        {opt.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  <FormHelperText>
                                    {optError?.message || " "}
                                  </FormHelperText>
                                </FormControl>
                              );
                            }}
                          />

                          {/* Free-text when "Other" is selected + helper */}
                          {(recValues?.[idx]?.option ?? "") === "Other" && (
                            <TextField
                              size="small"
                              label="Specify recommendation"
                              sx={{ flex: '1 1 260px', minWidth: 240 }}
                              error={!!errors?.recommendations?.[idx]?.otherText}
                              helperText={
                                errors?.recommendations?.[idx]?.otherText?.message || " "
                              }
                              {...register(`recommendations.${idx}.otherText`)}
                            />
                          )}

                          {/* Remove row */}
                          <Box sx={{ ml: 'auto' }}>
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => removeRec(idx)}
                              aria-label="Remove recommendation"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>                        
                      </Grid>                      
                    );
                  })}
                </Grid>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={recFields.length >= MAX_RECOMMENDATIONS}
                    onClick={() => {
                      if (recFields.length >= MAX_RECOMMENDATIONS) return;
                      appendRec({ option: "", other_text: "" });
                    }}
                    sx={{ mt: 2 }}
                  >
                    Add Recommendation ({recFields.length}/{MAX_RECOMMENDATIONS})
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Customer's Responsibilities Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    CUSTOMER'S RESPONSIBILITIES
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />

              <CardContent sx={{ px: 2, pb: 2 }}>
                {/* SANITATION */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    SANITATION
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Needs improvement (Specify area)
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label="Specify area 1"
                        value={customerResp.sanitation1}
                        onChange={handleCustomerRespChange('sanitation1')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label="Specify area 2"
                        value={customerResp.sanitation2}
                        onChange={handleCustomerRespChange('sanitation2')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label="Specify area 3"
                        value={customerResp.sanitation3}
                        onChange={handleCustomerRespChange('sanitation3')}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* RODENT ENTRY POINT */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    RODENT ENTRY POINT
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Needs improvement (Specify area)
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Sealing"
                        value={customerResp.sealing}
                        onChange={handleCustomerRespChange('sealing')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth size="small"
                        label="Barriers"
                        value={customerResp.barriers}
                        onChange={handleCustomerRespChange('barriers')}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label="Repairs & Maintenance (Specify area)"
                        value={customerResp.repairsMaintenance}
                        onChange={handleCustomerRespChange('repairsMaintenance')}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label="Renovation"
                        value={customerResp.renovation}
                        onChange={handleCustomerRespChange('renovation')}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Chemical Used Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            CHEMICAL/S USED
                          </TableCell>                             
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chemical_used.map((chemical, index) => (
                          <TableRow key={index}>
                            <TableCell>{chemical.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.chemical}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "chemical",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Control Against Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    CONTROL AGAINST
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                For Certificate
              </Typography>
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.crawlingInsects}
                        onChange={handleControlAgainstChange("crawlingInsects")}
                      />
                    }
                    label="Crawling Insects"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.flyingInsects}
                        onChange={handleControlAgainstChange("flyingInsects")}
                      />
                    }
                    label="Flying Insects"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.rodentControl}
                        onChange={handleControlAgainstChange("rodentControl")}
                      />
                    }
                    label="Rodent Control"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.others}
                        onChange={handleControlAgainstChange("others")}
                      />
                    }
                    label="Others"
                  />
                </FormGroup>

                {controlAgainst.others && (
                  <TextField
                    label="Please specify"
                    value={otherControlText}
                    onChange={(e) => setOtherControlText(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
              {errors?.control_against_count && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.control_against_count.message}
                </FormHelperText>
              )}
              {controlAgainst.others && errors?.control_against_others_text && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {errors.control_against_others_text.message}
                </FormHelperText>
              )}
            </Card>
          </Grid>
          <input type="hidden" {...register("control_against_count")} />
          <input type="hidden" {...register("control_against_others")} />
          <input type="hidden" {...register("control_against_others_text")} />
          
          {/* Accomplished By and Acknowledged By Signature Section */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    {/* Service Accomplished by Section */}
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Service Accomplished by:
                          </Typography>
                          <Button size="small" variant="outlined" onClick={openEditTechs} sx={{ mb: 2 }}>
                            Edit technicians
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {jobOrder?.technicians?.length > 0 ? (
                              jobOrder?.technicians.map((technician, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                  key={technician.id}
                                  mb={2}
                                >
                                  <TextField
                                    label={`Technician ${index + 1}`}
                                    variant="outlined"
                                    fullWidth
                                    value={`${technician.firstname} ${technician.lastname}`}
                                    disabled
                                    size="small"
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12} md={12} lg={12} xl={12}>
                                <TextField
                                  label="Technician"
                                  variant="outlined"
                                  fullWidth
                                  value="No Technicians Available"
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>                          
                          <Box
                            sx={{
                              width: canvasWidth,
                              height: ackHeight,
                              border: "2px solid #000",
                              padding: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <SignatureCanvas
                              ref={accomplishedBySignatureRef}
                              penColor="black"
                              canvasProps={{
                                width: canvasWidth,
                                height: ackHeight,
                                className: "sigCanvas",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Technician Signatory Dropdown */}
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal" error={!!errors.technician_signatory}>
                          <InputLabel id="technician-signatory-label">
                            Technician Signatory
                          </InputLabel>
                          <Select
                            labelId="technician-signatory-label"
                            id="accomplished-by-technician"
                            value={technicianSignatory}
                            label="Technician Signatory"
                            onChange={(e) => {
                              const v = e.target.value;
                              setTechnicianSignatory(v);
                              setValue("technician_signatory", v, { shouldValidate: true, shouldDirty: true });
                            }}
                          >
                            {jobOrder?.technicians?.map((tech) => (
                              <MenuItem
                                key={tech.id}
                                value={`${tech.firstname} ${tech.lastname}`}
                              >
                                {`${tech.firstname} ${tech.lastname}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.technician_signatory && (
                            <FormHelperText>{errors.technician_signatory.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      {/* Action Buttons for Accomplished Signature */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={clearAccomplishedSignature}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveAndUploadAccomplishedSignature}
                          sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      {(errors?.accomplishedUploaded || errors?.accomplishedSignature) && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors.accomplishedUploaded?.message || errors.accomplishedSignature?.message}
                        </FormHelperText>
                      )}

                      {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                      {!isTechnician && technicianSignatureUploadedAt && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            🔵 Technician Signature Uploaded At:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                            {new Date(technicianSignatureUploadedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                            This timestamp is saved and cannot be changed.
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule Section */}
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{                      
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      Schedule
                    </Typography>
                  }
                />
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Date"
                        name="job_date"
                        type="date"
                        value={jobOrderParameters.job_date}
                        onChange={(e) =>
                          handleJObOrderChange("job_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Time"
                        name="start_time"
                        type="time"
                        value={jobOrderParameters.start_time}
                        onChange={(e) =>
                          handleJObOrderChange("start_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        label="End Date"
                        name="end_date"
                        type="date"
                        value={jobOrderParameters.end_date}
                        onChange={(e) =>
                          handleJObOrderChange("end_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            min: jobOrderParameters.job_date || undefined
                          }
                        }}
                      />
                    </Grid> */}
                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        label="End Time"
                        name="end_time"
                        type="time"
                        value={jobOrderParameters.end_time}
                        onChange={(e) =>
                          handleJObOrderChange("end_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => handleJObOrderChange("end_time", getNowHHMM())}
                              >
                                Now
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid> */}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* For Client Only Section */}
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="subtitle2"
                                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      For Client Only
                    </Typography>
                  }
                  sx={{ px: 2, pt: 2, pb: 0 }}
                />

                <CardContent sx={{ px: 2, pb: 2 }}>
                  {/* Satisfaction label */}
                  <Typography variant="body2">
                    Your Satisfaction Rating:                    
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    (1 – lowest, 5 – highest)
                  </Typography>

                  {/* Rating buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    {[1, 2, 3, 4, 5].map((n) => {
                      const selected = satisfactionRating === n;
                      return (
                        <Button
                          key={n}
                          onClick={() => {
                            setSatisfactionRating(n);
                            setjobOrderParameters(prev => ({
                              ...prev,
                              satisfaction_rating: n
                            }));
                            // inform RHF so validation passes & the error disappears
                            setValue("satisfaction_rating", n, { shouldValidate: true, shouldDirty: true });
                            clearErrors("satisfaction_rating");
                          }}
                          sx={{
                            fontSize: '1.25rem',
                            minWidth: '40px',
                            height: '40px',
                            p: 0,
                            borderRadius: '50%',
                            transition: 'all 0.2s ease-in-out',
                            // selected styles
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'common.white' : 'text.secondary',
                            border: selected ? 'none' : '1px solid',
                            borderColor: selected ? 'transparent' : 'divider',
                            transform: selected ? 'scale(1.2)' : 'scale(1)',
                            '&:hover': {
                              bgcolor: selected ? 'primary.dark' : 'grey.100',
                            },
                            cursor: 'pointer'
                          }}
                        >
                          {n}
                        </Button>
                      );
                    })}
                  </Box>
                  {errors?.satisfaction_rating && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.satisfaction_rating.message}
                    </FormHelperText>
                  )}
                
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Acknowledged By:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>                        
                        <Box
                          sx={{
                            width: ackWidth,
                            height: ackHeight,
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: ackWidth,
                              height: ackHeight,
                              className: "sigCanvas",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ marginTop: 2 }}>
                      <Grid item xs={12} md={12} lg={12}>                        
                        <TextField
                          fullWidth
                          label="Client Representative"
                          name="Client Representative"
                          variant="outlined"
                          value={acknowledgedClientSignatory}
                          onChange={(e) => {
                            const v = e.target.value;
                            setAcknowledgedClientSignatory(v);
                            setValue("acknowledgedClientSignatory", v, { shouldValidate: true, shouldDirty: true });
                          }}
                          size="small"
                        />
                        {errors?.acknowledgedClientSignatory && (
                          <FormHelperText error>{errors.acknowledgedClientSignatory.message}</FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    {/* Action Buttons for Acknowledged Signature */}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      mt={2}
                      mb={2}
                      mr={2}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={clearAcknowledgedSignature}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FF4C4C",
                          borderColor: "#FF4C4C",
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveAndUploadAcknowledgedSignature}
                        sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                      >
                        Upload
                      </Button>
                    </Grid>
                    {(errors?.acknowledgedUploaded || errors?.acknowledgedSignature) && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.acknowledgedUploaded?.message || errors.acknowledgedSignature?.message}
                      </FormHelperText>
                    )}

                    {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
                    {!isTechnician && clientSignatureUploadedAt && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          ✅ Client Signature Uploaded At:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                          {new Date(clientSignatureUploadedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#558b2f', mt: 0.5, display: 'block' }}>
                          This timestamp is saved and cannot be changed.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </CardContent>

              </Card>
            </Grid>
          </Grid>
          
          {/* Dialog for Edit Technicians */}
          <Dialog open={editTechsOpen} onClose={closeEditTechs} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Technicians</DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableBody>
                  {allTechnicians.map(t => {
                    const notes = [];
                    if (t.competency_level?.driving_4_wheels && t.competency_level.driving_4_wheels !== "N/A") {
                      notes.push("Driver: 4 wheels");
                    }
                    if (t.competency_level?.driving_2_wheels && t.competency_level.driving_2_wheels !== "N/A") {
                      notes.push("Driver: 2 wheels");
                    }
                    const checked = editSelectedTechIds.includes(t.id);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked} onChange={toggleEditTech(t.id)} />
                        </TableCell>
                        <TableCell>{`${t.lastname}, ${t.firstname}`}</TableCell>
                        <TableCell>{notes.join(", ")}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditTechs}>Cancel</Button>
              <Button variant="contained" disabled={!editSelectedTechIds.length} onClick={saveEditTechs}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Display Receipts and Reports Links */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {links?.receipts_url ||
                    links?.certificate_url ? (
                    <>
                      {links?.receipts_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.receipts_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipts.pdf{" "}
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.certificate_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.certificate_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Certificate.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {/* {links?.reports_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.reports_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Reports.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )} */}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-service-reports")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
        {/* Hidden validators for signatures + rating */}
        <Controller name="acknowledgedSignature" control={control}
          rules={{ required: "Please click Upload for Acknowledged signature." }}
          render={() => null}
        />
        <Controller name="acknowledgedUploaded" control={control}
          rules={{ validate: v => v === true || "Please click Upload for Acknowledged signature." }}
          render={() => null}
        />

        <Controller name="accomplishedSignature" control={control}
          rules={{ required: "Please click Upload for Accomplished signature." }}
          render={() => null}
        />
        <Controller name="accomplishedUploaded" control={control}
          rules={{ validate: v => v === true || "Please click Upload for Accomplished signature." }}
          render={() => null}
        />

        <Controller name="satisfaction_rating" control={control}
          rules={{
            required: "Please pick a rating (1–5).",
            validate: (v) => Number(v) >= 1 || "Please pick a rating (1–5).",
          }}
          render={() => null}
        />

        <Controller
          name="technician_signatory"
          control={control}
          rules={{ required: "Please choose a technician signatory." }}
          render={() => null}
        />

        <Controller
          name="acknowledgedClientSignatory"
          control={control}
          rules={{ required: "Please enter the client representative." }}
          render={() => null}
        />
      </form>
    </BaseFormWrapper>
  );
}

export default QcForm;
